import { Category, CellData, FinancialEntry, Sheet } from "@/types/finance";
import { authApiV2 } from "./apiAuth";

// const API_URL_V2 = "https://back-pochete-t7kmb.ondigitalocean.app/api/v2";
// const API_URL_V2 = "http://localhost:3000/api/v2";
const API_URL_V2 = "https://backend.pochete.app/api/v2";
// const { updateEntry, updateCellData } = useFinance();

// Função auxiliar para obter o token de autorização
const getAuthToken = async (): Promise<string | null> => {
  return authApiV2.getUser().token;
};

// API para Categorias
export const sheetsApiV2 = {
  // Obter todas as categorias do usuário
  getAll: async (): Promise<Sheet[]> => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Usuário não autenticado");
    }

    const response = await fetch(`${API_URL_V2}/sheet`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Erro ao buscar categorias");
    }

    const { data } = await response.json();
    // Mapear os dados do formato do banco de dados para o formato da aplicação
    return data.map((item: any) => ({
      id: item.id,
      title: item.title,
      user_id: item.user_id,
      principal: item.principal,
      created_at: item.created_at,
    }));
  },

  updateTitle: async (id: string, title: string): Promise<void> => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Usuário não autenticado");
    }

    const response = await fetch(`${API_URL_V2}/sheet/${id}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ title }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Erro ao atualizar título da planilha"
      );
    }
  },
};

// API para Categorias
export const categoriesApiV2 = {
  // Obter todas as categorias do usuário
  getAll: async (sheetId: string): Promise<Category[]> => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Usuário não autenticado");
    }

    const response = await fetch(
      `${API_URL_V2}/categories/bysheet/${sheetId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Erro ao buscar categorias");
    }

    const { data } = await response.json();

    const sortedCategories = data.sort((a: any, b: any) => {
      // Sort by type (income, expense, investment) and then by value
      if (a.type !== b.type) {
        const typeOrder = { income: 1, expense: 2, investment: 3 };
        return typeOrder[a.type] - typeOrder[b.type];
      }

      // sort by name ascending
      return a.name.localeCompare(b.name);
    });
    // Mapear os dados do formato do banco de dados para o formato da aplicação
    return sortedCategories.map((item: any) => ({
      id: item.id,
      name: item.name,
      type: item.type,
      parentId: item.parent_id,
      order: item.order_position,
      sheet_id: item.sheet_id,
      isExpanded: true,
    }));
  },

  // Obter uma categoria específica
  getById: async (id: string): Promise<Category> => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Usuário não autenticado");
    }

    const response = await fetch(`${API_URL_V2}/categories/${id}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Erro ao buscar categoria");
    }

    const { data } = await response.json();
    return {
      id: data.id,
      name: data.name,
      type: data.type,
      parentId: data.parent_id,
      order: data.order_position,
      sheet_id: data.sheet_id,
    };
  },

  // Criar uma nova categoria
  create: async (category: Omit<Category, "id">): Promise<Category> => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Usuário não autenticado");
    }

    const response = await fetch(`${API_URL_V2}/categories`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: category.name,
        type: category.type,
        parent_id: category.parentId,
        order_position: category.order,
        sheet_id: category.sheet_id,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Erro ao criar categoria");
    }

    const { data } = await response.json();
    return {
      id: data.id,
      name: data.name,
      type: data.type,
      parentId: data.parent_id,
      order: data.order_position,
      sheet_id: data.sheet_id,
    };
  },

  // Atualizar uma categoria existente
  update: async (
    id: string,
    category: Partial<Category>
  ): Promise<Category> => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Usuário não autenticado");
    }

    const payload: any = {};
    if (category.name) payload.name = category.name;
    if (category.type) payload.type = category.type;
    if (category.parentId !== undefined) payload.parent_id = category.parentId;
    if (category.order !== undefined) payload.order_position = category.order;
    if (category.sheet_id !== undefined) payload.sheet_id = category.sheet_id;

    const response = await fetch(`${API_URL_V2}/categories/${id}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Erro ao atualizar categoria");
    }

    const { data } = await response.json();
    return {
      id: data.id,
      name: data.name,
      type: data.type,
      parentId: data.parent_id,
      order: data.order_position,
      sheet_id: data.sheet_id,
    };
  },

  // Excluir uma categoria
  delete: async (id: string): Promise<void> => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Usuário não autenticado");
    }

    const response = await fetch(`${API_URL_V2}/categories/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Erro ao excluir categoria");
    }
  },
};

// API para Lançamentos Financeiros
export const financialEntriesApiV2 = {
  // Obter todos os lançamentos do usuário
  getAll: async (sheetId: string): Promise<FinancialEntry[]> => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Usuário não autenticado");
    }

    const response = await fetch(
      `${API_URL_V2}/financial-entries/bysheet/${sheetId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Erro ao buscar lançamentos");
    }

    const { data } = await response.json();
    // Mapear os dados do formato do banco de dados para o formato da aplicação
    return data.map((item: any) => ({
      id: item.id,
      category_id: item.category_id,
      amount: Number(item.amount),
      year: item.year,
      month: item.month,
      date: item.date ? new Date(item.date) : undefined,
      description: item.description,
      sheet_id: item.sheet_id,
      status: item.status,
    }));
  },

  // Obter um lançamento específico
  getById: async (id: string): Promise<FinancialEntry> => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Usuário não autenticado");
    }

    const response = await fetch(`${API_URL_V2}/financial-entries/${id}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Erro ao buscar lançamento");
    }

    const { data } = await response.json();
    return {
      id: data.id,
      category_id: data.category_id,
      amount: Number(data.amount),
      year: data.year,
      month: data.month,
      date: data.date ? new Date(data.date) : undefined,
      description: data.description,
      sheet_id: data.sheet_id,
      status: data.status,
    };
  },

  atualizarInserir: async (cellData: CellData): Promise<FinancialEntry> => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Usuário não autenticado");
    }

    const response = await fetch(`${API_URL_V2}/financial-entries`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        category_id: cellData.category.id,
        amount: cellData.amount,
        year: cellData.year,
        month: cellData.month,
        description: cellData.description,
        sheet_id: cellData.sheet_id,
        status: cellData.status,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Erro ao criar lançamento");
    }

    const resp = await response.json();
    return resp.data;
  },
};
